import { useState, useEffect, useRef, useCallback } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';
import { preCacheEndesa, getCachedBlob } from '../utils/endesaFileCache';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];

const DOC_TABS = ['ptw', 'prejob', 'postjob', 'historial'];

const TAB_LABELS = {
  ficha:    'FICHA',
  ptw:      '🔐 PTW',
  prejob:   '🛡️ Prejob',
  postjob:  '✅ Postjob',
  historial:'🗂️ Historial',
};

const IFRAME_SRC = { ptw: '/ptw.html', prejob: '/prejob.html', postjob: '/postjob.html' };

const TIPO_CONFIG = {
  ptw:     { icon: '🔐', label: 'Permisos de Trabajo', color: '#f472b6' },
  prejob:  { icon: '🛡️', label: 'Prejob',              color: '#60a5fa' },
  postjob: { icon: '✅', label: 'Postjob',             color: '#4ade80' },
};

const HISTORIAL_TIPOS = new Set(['ptw', 'prejob', 'postjob']);

// ── Helpers ───────────────────────────────────────────────────────────────────


async function downloadEndesa(id, fname, cloudinaryUrl) {
  try {
    if (cloudinaryUrl) {
      // Try offline cache first
      const cached = await getCachedBlob(id);
      if (cached) {
        const blobUrl = URL.createObjectURL(cached);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.href = blobUrl; a.download = fname || 'documento'; a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        return;
      }
      // Open blank window BEFORE await — keeps user-gesture context, avoids popup blocker
      const newWin = window.open('', '_blank');
      const { url } = await api.get(`/api/endesa/${id}/signed-url`);
      if (url) { if (newWin) { newWin.location.href = url; return; } window.open(url, '_blank', 'noreferrer'); return; }
      if (newWin) newWin.close();
    }
    const blob = await api.get(`/api/endesa/${id}/file`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.href = blobUrl; a.download = fname || 'documento'; a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (e) { alert('No se pudo descargar: ' + (e.message || e)); }
}

async function downloadDoc(doc) {
  try {
    const res = await api.get(`/api/documents/${doc.id}/file`, { responseType: 'blob' });
    const url = URL.createObjectURL(res);
    const a = document.createElement('a'); a.href = url; a.download = doc.file_name || `documento-${doc.id}`; a.click();
    URL.revokeObjectURL(url);
  } catch { alert('Archivo no disponible'); }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function DocCard({ doc, onDelete, isAdmin, onPreview }) {
  const ext  = (doc.file_name || '').split('.').pop().toUpperCase();
  const icon = ext === 'PDF' ? '📄' : ['JPG','JPEG','PNG'].includes(ext) ? '🖼️' : '📎';
  const previewable = ['PDF','JPG','JPEG','PNG'].includes(ext);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px' }}>
      <div style={{ width: 34, height: 34, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(251,191,36,.1)', border: '1px solid rgba(251,191,36,.2)', fontSize: 16, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name || 'documento'}</div>
        <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{new Date(doc.created_at).toLocaleDateString('es-ES')}{doc.user_nombre && ` · ${doc.user_nombre}`}</div>
      </div>
      {previewable && (
        <button onClick={() => onPreview(doc.id, doc.file_name, doc.cloudinary_url)} style={{ background: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.3)', color: '#a78bfa', padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>👁 Ver</button>
      )}
      <button onClick={() => downloadEndesa(doc.id, doc.file_name, doc.cloudinary_url)} style={{ background: 'rgba(96,165,250,.12)', border: '1px solid rgba(96,165,250,.3)', color: '#60a5fa', padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>↓</button>
      {isAdmin && <button onClick={() => onDelete(doc.id)} style={{ background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', color: '#f87171', padding: '5px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>🗑</button>}
    </div>
  );
}

function UploadRow({ label, onUpload, uploading }) {
  const fileRef = useRef(null);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" ref={fileRef}
        style={{ fontSize: 11, color: 'var(--text-dim)', flex: 1, minWidth: 160, background: 'var(--surface-deep)', border: '1px dashed var(--border)', borderRadius: 6, padding: '6px 8px' }} />
      <button onClick={() => { if (!fileRef.current?.files?.length) return; onUpload(fileRef.current.files[0], () => { fileRef.current.value = ''; }); }}
        disabled={uploading}
        style={{ background: 'var(--amber)', color: '#000', border: 'none', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 800, fontSize: 11, flexShrink: 0 }}>
        {uploading ? '…' : `+ ${label}`}
      </button>
    </div>
  );
}

function DocRow({ d, onDelete }) {
  return (
    <div style={{ background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nombre || d.tipo}</div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
          {new Date(d.created_at).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
          {d.semana && <span> · Sem {d.semana}/{d.año}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {d.has_file && (
          <button onClick={() => downloadDoc(d)} title="Descargar"
            style={{ background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>⬇</button>
        )}
        <button onClick={() => onDelete(d.id)} title="Eliminar"
          style={{ background: 'rgba(248,113,113,.12)', border: '1px solid rgba(248,113,113,.4)', color: '#f87171', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>🗑</button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Endesa() {
  const user    = useAuthStore(s => s.user);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);

  const [tab,              setTab]              = useState('ficha');
  const [loading,          setLoading]          = useState(true);
  const [uploading,        setUploading]        = useState(false);
  const [preview,          setPreview]          = useState(null);  // { url, fname, ext, id }
  const [loadingPrev,      setLoadingPrev]      = useState(false);

  // Endesa data
  const [docs,             setDocs]             = useState([]);
  const [employees,        setEmployees]        = useState([]);
  const [selectedEmpId,    setSelectedEmpId]    = useState('');
  const [fichaSede,        setFichaSede]        = useState('sevilla');
  const [newVehicle,       setNewVehicle]       = useState('');
  const newVehicleFileRef  = useRef(null);

  // Documentos/historial data
  const [historialDocs,    setHistorialDocs]    = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    async function init() {
      setLoading(true);
      const [docsRes, empsRes] = await Promise.all([
        api.get('/api/endesa/docs').catch(() => ({ data: [] })),
        isAdmin ? api.get('/api/employees/list').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      const empList = (empsRes.data || []).filter(e =>
        (!e.estado || e.estado !== 'baja') &&
        (!e.user_rol || e.user_rol === 'tecnico')
      );
      const allDocs = docsRes.data || [];
      setDocs(allDocs);
      setEmployees(empList);
      const firstSev = empList.find(e => (e.sede || 'sevilla') === 'sevilla');
      if (firstSev) setSelectedEmpId(String(firstSev.id));
      else if (empList.length > 0) setSelectedEmpId(String(empList[0].id));
      setLoading(false);
      // Pre-cache binary files for offline access (cloudinary and server-hosted)
      preCacheEndesa(
        allDocs,
        id => api.get(`/api/endesa/${id}/signed-url`),
        id => api.get(`/api/endesa/${id}/file`, { responseType: 'blob' })
      ).catch(() => {});
    }
    init();
  }, []); // eslint-disable-line

  // ── Load historial when tab changes ──────────────────────────────────────
  useEffect(() => {
    if (tab !== 'historial') return;
    setLoadingHistorial(true);
    api.get('/api/documents/mine')
      .then(r => setHistorialDocs(r.data || []))
      .catch(() => setHistorialDocs([]))
      .finally(() => setLoadingHistorial(false));
  }, [tab]);

  // ── Listen for iframe document saves ─────────────────────────────────────
  useEffect(() => {
    function onMsg(e) {
      if (!e.data || e.data.type !== 'sgv_doc') return;
      const d = e.data;
      const tipoMap = { ptw: 'ptw', prejob: 'prejob', postjob: 'postjob' };
      if (!tipoMap[d.docType]) return;
      api.post('/api/documents/create', {
        tipo: tipoMap[d.docType],
        nombre: d.nombre || null,
        año: d.año || null,
        semana: d.semana || null,
        total_gastos: 0,
        nota_numero: null,
        file_b64: d.fileB64 || null,
        file_name: d.fileName || null,
      }).catch(() => {});
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // ── Endesa actions ────────────────────────────────────────────────────────
  const reloadEndesa = useCallback(async () => {
    const r = await api.get('/api/endesa/docs').catch(() => ({ data: [] }));
    setDocs(r.data || []);
  }, []);

  async function doUpload({ seccion, subseccion, doc_tipo, employee_id }, file, reset) {
    setUploading(true);
    try {
      // 1. Obtener firma del backend (sin pasar el archivo por Vercel)
      const sign = await api.get('/api/endesa/sign-upload');
      const { timestamp, folder, signature, api_key, cloud_name } = sign;

      // 2. Upload directo browser → Cloudinary (sin límite de Vercel)
      const ext          = (file.name || '').split('.').pop().toLowerCase();
      const IMAGE_EXTS   = new Set(['jpg','jpeg','png','gif','webp','bmp','tiff','svg']);
      // El QR siempre se sube como resource_type "image" (aunque sea un PDF):
      // así Cloudinary puede generar una miniatura real de la página 1 en vez
      // de quedar como archivo "raw" sin vista previa posible.
      const resourceType = (doc_tipo === 'qr' || IMAGE_EXTS.has(ext)) ? 'image' : 'raw';
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', api_key);
      fd.append('timestamp', String(timestamp));
      fd.append('signature', signature);
      fd.append('folder', folder);

      const cldRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/${resourceType}/upload`,
        { method: 'POST', body: fd }
      );
      if (!cldRes.ok) {
        const err = await cldRes.json().catch(() => ({}));
        throw new Error(err?.error?.message || `Cloudinary error ${cldRes.status}`);
      }
      const cld = await cldRes.json();

      // 3. Guardar solo metadatos en nuestro backend (JSON pequeño)
      await api.post('/api/endesa/metadata', {
        seccion,
        subseccion:   subseccion   || null,
        doc_tipo:     doc_tipo     || null,
        employee_id:  employee_id  || null,
        file_name:    file.name,
        cloudinary_url: cld.secure_url,
        cloudinary_id:  cld.public_id,
      });

      reset();
      await reloadEndesa();
    } catch (e) { alert('Error al subir: ' + e.message); }
    finally { setUploading(false); }
  }

  async function openPreview(id, fname, cloudinaryUrl) {
    const ext      = (fname || '').split('.').pop().toLowerCase();
    const isMobile = window.matchMedia('(pointer: coarse)').matches;

    // Helper: try cache then network for a blob URL
    async function resolveBlobUrl() {
      if (cloudinaryUrl) {
        const cached = await getCachedBlob(id);
        if (cached) return URL.createObjectURL(cached);
        const r = await api.get(`/api/endesa/${id}/signed-url`);
        if (r.url) return r.url;
      }
      const blob = await api.get(`/api/endesa/${id}/file`, { responseType: 'blob' });
      return URL.createObjectURL(blob);
    }

    if (isMobile) {
      const newWin = window.open('', '_blank');
      try {
        const url = await resolveBlobUrl();
        if (url) { if (newWin) newWin.location.href = url; else window.open(url, '_blank', 'noreferrer'); }
        else { if (newWin) newWin.close(); alert('Archivo no disponible'); }
      } catch (e) { if (newWin) newWin.close(); alert('No se pudo cargar el archivo: ' + (e.message || e)); }
      return;
    }

    // Desktop
    setLoadingPrev(true);
    try {
      const url = await resolveBlobUrl();
      const isCld = cloudinaryUrl && url.startsWith('http');
      setPreview({ id, url, fname: fname || 'documento', ext, isCld, cloudinaryUrl: isCld ? cloudinaryUrl : null });
    } catch (e) { alert('No se pudo cargar la vista previa: ' + (e.message || e)); }
    setLoadingPrev(false);
  }

  function closePreview() {
    if (preview?.url && !preview.isCld) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  async function delEndesa(id) {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try { await api.delete(`/api/endesa/${id}`); await reloadEndesa(); }
    catch (e) { alert(e.message); }
  }

  async function delHistorial(id) {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try { await api.delete(`/api/documents/${id}`); setHistorialDocs(prev => prev.filter(d => d.id !== id)); }
    catch { alert('No se pudo eliminar'); }
  }

  // ── Derived data ──────────────────────────────────────────────────────────
  const fichaDocs = isAdmin
    ? docs.filter(d => d.seccion === 'ficha' && String(d.employee_id) === selectedEmpId)
    : docs.filter(d => d.seccion === 'ficha' && String(d.user_id) === String(user.id));
  const carnetDocs  = fichaDocs.filter(d => d.subseccion === 'carnet');
  const episDocs    = fichaDocs.filter(d => d.subseccion === 'epis');

  const cochesGroups = {};
  docs.filter(d => d.seccion === 'coches').forEach(d => {
    const v = d.subseccion || '—';
    if (!cochesGroups[v]) cochesGroups[v] = { qr: [], otros: [] };
    (d.doc_tipo === 'qr' ? cochesGroups[v].qr : cochesGroups[v].otros).push(d);
  });

  const pepDocs = docs.filter(d => d.seccion === 'pep');
  const pepInst = pepDocs.filter(d => d.subseccion === 'instalacion');
  const pepMant = pepDocs.filter(d => d.subseccion === 'mantenimiento');

  const histGrupos = {};
  historialDocs.filter(d => HISTORIAL_TIPOS.has(d.tipo)).forEach(d => {
    if (!histGrupos[d.tipo]) histGrupos[d.tipo] = [];
    histGrupos[d.tipo].push(d);
  });

  const isIframeTab = IFRAME_SRC[tab] != null;

  if (loading) return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>;

  const isImg = preview && ['jpg','jpeg','png','gif','webp'].includes(preview?.ext);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Preview modal ────────────────────────────────────────────────── */}
      {(preview || loadingPrev) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,.93)', display: 'flex', flexDirection: 'column' }} onClick={preview ? closePreview : undefined}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.1)', flexShrink: 0, background: 'rgba(13,16,32,.95)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#d8e2f4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 12 }}>
              {loadingPrev ? 'Cargando…' : preview?.fname}
            </div>
            {preview && (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => downloadEndesa(preview.id, preview.fname, preview.cloudinaryUrl)}
                  style={{ background: 'rgba(96,165,250,.15)', border: '1px solid rgba(96,165,250,.4)', color: '#60a5fa', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                  ↓ Descargar
                </button>
                <button onClick={closePreview}
                  style={{ background: 'rgba(248,113,113,.15)', border: '1px solid rgba(248,113,113,.4)', color: '#f87171', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                  ✕ Cerrar
                </button>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isImg ? 16 : 0 }}
            onClick={e => e.stopPropagation()}>
            {loadingPrev ? (
              <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>Cargando documento…</div>
            ) : isImg ? (
              <img src={preview.url} alt={preview.fname}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8, boxShadow: '0 4px 40px rgba(0,0,0,.8)' }} />
            ) : (
              /* iframe es más compatible que embed en móvil (iOS/Android) */
              <iframe src={preview.url} title={preview.fname}
                style={{ width: '100%', height: '100%', border: 'none' }} />
            )}
          </div>
        </div>
      )}

      {/* ── Tab bar ─────────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 16px', flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', padding: '14px 0 0' }}>ENDESA</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <button
            onClick={() => setTab('ficha')}
            style={{
              padding: '9px 16px', fontWeight: 800, fontSize: 11, cursor: 'pointer',
              border: 'none', borderBottom: tab === 'ficha' ? '2px solid var(--amber)' : '2px solid transparent',
              background: 'transparent', color: tab === 'ficha' ? 'var(--amber)' : 'var(--text-dim)',
              marginBottom: -1, whiteSpace: 'nowrap',
            }}
          >FICHA</button>
          <select
            value={DOC_TABS.includes(tab) ? tab : ''}
            onChange={e => { if (e.target.value) setTab(e.target.value); }}
            style={{
              background: 'var(--surface2)',
              border: DOC_TABS.includes(tab) ? '1.5px solid var(--amber)' : '1.5px solid var(--border)',
              color: DOC_TABS.includes(tab) ? 'var(--amber)' : 'var(--text-dim)',
              borderRadius: 6, padding: '7px 10px', fontSize: 11, fontWeight: 800,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <option value="">Documentos</option>
            {DOC_TABS.map(t => <option key={t} value={t}>{TAB_LABELS[t]}</option>)}
          </select>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', overflowY: isIframeTab ? 'hidden' : 'auto' }}>

        {/* Iframe tabs (PTW / Prejob / Postjob) */}
        {Object.entries(IFRAME_SRC).map(([key, src]) => (
          <iframe
            key={key}
            src={src}
            title={TAB_LABELS[key]}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              border: 'none',
              display: tab === key ? 'block' : 'none',
            }}
          />
        ))}

        {/* Scrollable endesa + historial tabs */}
        {!isIframeTab && (
          <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 900, margin: '0 auto' }}>

            {/* ── FICHA ─────────────────────────────────────────────────── */}
            {tab === 'ficha' && (
              <div style={{ display: 'grid', gap: 16 }}>
                {isAdmin && employees.length > 0 && (
                  <div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      {['sevilla','granada'].map(s => (
                        <button key={s} onClick={() => {
                          setFichaSede(s);
                          const first = employees.find(e => (e.sede || 'sevilla') === s);
                          if (first) setSelectedEmpId(String(first.id));
                        }} style={{
                          padding: '5px 14px', borderRadius: 6, fontWeight: 800, fontSize: 11, cursor: 'pointer',
                          textTransform: 'capitalize',
                          background: fichaSede === s ? 'var(--amber)' : 'var(--surface2)',
                          color: fichaSede === s ? '#000' : 'var(--text-dim)',
                          border: fichaSede === s ? 'none' : '1px solid var(--border)',
                        }}>{s.charAt(0).toUpperCase()+s.slice(1)}</button>
                      ))}
                    </div>
                    <label style={LBL}>Empleado</label>
                    <select value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)} style={SEL}>
                      {employees
                        .filter(e => (e.sede || 'sevilla') === fichaSede)
                        .map(e => <option key={e.id} value={String(e.id)}>{e.nombre}</option>)}
                    </select>
                  </div>
                )}
                {!isAdmin && (
                  <div style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 4 }}>
                    Documentos ENDESA de <strong style={{ color: 'var(--text)' }}>{user.nombre}</strong>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 16 }}>
                  <div style={CARD}>
                    <div style={STITLE}>🪪 Carnet Endesa</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {carnetDocs.length === 0
                        ? <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '8px 0' }}>Sin documento</div>
                        : carnetDocs.map(d => <DocCard key={d.id} doc={d} onDelete={delEndesa} isAdmin={isAdmin} onPreview={openPreview} />)}
                    </div>
                    {isAdmin && <UploadRow key={`carnet-${selectedEmpId}`} label="Carnet" uploading={uploading} onUpload={(file, reset) => doUpload({ seccion: 'ficha', subseccion: 'carnet', employee_id: selectedEmpId }, file, reset)} />}
                  </div>
                  <div style={CARD}>
                    <div style={STITLE}>🦺 EPIs</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {episDocs.length === 0
                        ? <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '8px 0' }}>Sin documentos</div>
                        : episDocs.map(d => <DocCard key={d.id} doc={d} onDelete={delEndesa} isAdmin={isAdmin} onPreview={openPreview} />)}
                    </div>
                    {isAdmin && <UploadRow key={`epis-${selectedEmpId}`} label="EPIs" uploading={uploading} onUpload={(file, reset) => doUpload({ seccion: 'ficha', subseccion: 'epis', employee_id: selectedEmpId }, file, reset)} />}
                  </div>
                </div>
                {isAdmin && employees.length === 0 && (
                  <div style={{ color: 'var(--text-dim)', fontSize: 13, textAlign: 'center', padding: 32 }}>No hay empleados</div>
                )}

                {/* ── PEP ─────────────────────────────────────────────── */}
                <div style={{ marginTop: 8 }}>
                  <div style={SECTION_HDR}>📋 PEP</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 16 }}>
                    <div style={CARD}>
                      <div style={STITLE}>🏗 PEP SET Y EDIFICIOS</div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {pepInst.length === 0
                          ? <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '8px 0' }}>Sin documento</div>
                          : pepInst.map(d => <DocCard key={d.id} doc={d} onDelete={delEndesa} isAdmin={isAdmin} onPreview={openPreview} />)}
                      </div>
                      {isAdmin && <UploadRow label="Instalación" uploading={uploading} onUpload={(file, reset) => doUpload({ seccion: 'pep', subseccion: 'instalacion' }, file, reset)} />}
                    </div>
                    <div style={CARD}>
                      <div style={STITLE}>🔧 PEP PFV, UPH, TP, ETC.</div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {pepMant.length === 0
                          ? <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '8px 0' }}>Sin documento</div>
                          : pepMant.map(d => <DocCard key={d.id} doc={d} onDelete={delEndesa} isAdmin={isAdmin} onPreview={openPreview} />)}
                      </div>
                      {isAdmin && <UploadRow label="Mantenimiento" uploading={uploading} onUpload={(file, reset) => doUpload({ seccion: 'pep', subseccion: 'mantenimiento' }, file, reset)} />}
                    </div>
                  </div>
                </div>

                {/* ── COCHES ──────────────────────────────────────────── */}
                <div style={{ marginTop: 8 }}>
                  <div style={SECTION_HDR}>🚗 Vehículos</div>
                  {isAdmin && (
                    <div style={{ ...CARD, marginBottom: 14 }}>
                      <div style={STITLE}>Añadir vehículo</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
                        <div style={{ flex: 1, minWidth: 160 }}>
                          <label style={LBL}>Matrícula / nombre</label>
                          <input value={newVehicle} onChange={e => setNewVehicle(e.target.value)} placeholder="Ej: 1234 ABC" style={SEL} />
                        </div>
                        <div style={{ flex: 2, minWidth: 180 }}>
                          <label style={LBL}>Archivo</label>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" ref={newVehicleFileRef}
                            style={{ fontSize: 11, color: 'var(--text-dim)', background: 'var(--surface-deep)', border: '1px dashed var(--border)', borderRadius: 6, padding: '8px 8px', width: '100%', boxSizing: 'border-box' }} />
                        </div>
                        <button disabled={uploading} onClick={async () => {
                          const v = newVehicle.trim(), f = newVehicleFileRef.current?.files?.[0];
                          if (!v) { alert('Introduce el nombre del vehículo'); return; }
                          if (!f) { alert('Selecciona un archivo'); return; }
                          await doUpload({ seccion: 'coches', subseccion: v }, f, () => {
                            newVehicleFileRef.current.value = ''; setNewVehicle('');
                          });
                        }} style={{ background: 'var(--amber)', color: '#000', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 800, fontSize: 12, flexShrink: 0, alignSelf: 'flex-end' }}>
                          {uploading ? '…' : '+ Añadir'}
                        </button>
                      </div>
                    </div>
                  )}
                  {Object.keys(cochesGroups).length === 0
                    ? <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '12px 0' }}>Sin documentos de vehículos</div>
                    : <div style={{ display: 'grid', gap: 14 }}>
                        {Object.entries(cochesGroups).map(([vehicle, { qr, otros }]) => (
                          <div key={vehicle} style={CARD}>
                            <div style={STITLE}>🚗 {vehicle}</div>

                            {/* QR — visible para todos los roles */}
                            <div style={{ display: 'grid', gap: 8, marginBottom: isAdmin ? 12 : 0 }}>
                              <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>📱 QR del vehículo</div>
                              {qr.length === 0
                                ? <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '8px 0' }}>Sin QR subido</div>
                                : qr.map(d => <DocCard key={d.id} doc={d} onDelete={delEndesa} isAdmin={isAdmin} onPreview={openPreview} />)}
                              {isAdmin && <UploadRow key={`coches-qr-${vehicle}`} label="QR" uploading={uploading} onUpload={(file, reset) => doUpload({ seccion: 'coches', subseccion: vehicle, doc_tipo: 'qr' }, file, reset)} />}
                            </div>

                            {/* Resto de documentación — solo admin, jefe, administrativo */}
                            {isAdmin && (
                              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                                <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>📄 Otra documentación</div>
                                <div style={{ display: 'grid', gap: 8 }}>
                                  {otros.length === 0
                                    ? <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '8px 0' }}>Sin documentos</div>
                                    : otros.map(d => <DocCard key={d.id} doc={d} onDelete={delEndesa} isAdmin={isAdmin} onPreview={openPreview} />)}
                                </div>
                                <UploadRow key={`coches-${vehicle}`} label="Documento" uploading={uploading} onUpload={(file, reset) => doUpload({ seccion: 'coches', subseccion: vehicle }, file, reset)} />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                  }
                </div>
              </div>
            )}

            {/* ── HISTORIAL ─────────────────────────────────────────────── */}
            {tab === 'historial' && (
              loadingHistorial ? (
                <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>
              ) : !Object.keys(histGrupos).length ? (
                <div style={{ padding: 40, color: 'var(--text-dim)', textAlign: 'center' }}>Sin documentos generados aún.</div>
              ) : (
                <div>
                  {Object.entries(histGrupos).map(([tipo, items]) => {
                    const cfg = TIPO_CONFIG[tipo] || { icon: '📎', label: tipo, color: 'var(--text)' };
                    return (
                      <div key={tipo} style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 10, color: cfg.color, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                          {cfg.icon} {cfg.label}
                        </div>
                        <div style={{ display: 'grid', gap: 6 }}>
                          {items.map(d => <DocRow key={d.id} d={d} onDelete={delHistorial} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

          </div>
        )}
      </div>
    </div>
  );
}

const CARD        = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' };
const SECTION_HDR = { fontSize: 12, fontWeight: 800, color: 'var(--text)', borderBottom: '1px solid var(--border)', paddingBottom: 8, marginBottom: 12 };
const SEL    = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' };
const STITLE = { fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 };
const LBL    = { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 4 };
